import React, { useMemo } from "react";
import { Lock } from "@mui/icons-material";
import { Box } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetBroadbandProvidersQuery } from "fond/api";
import { AreaDrawPanel, AreaPanel, AssessPanel, ScoringPanel, SubareaListPanel } from "fond/cityPlanner";
import {
  assessConfigurationHasValues,
  baseScoringInputConfigs,
  createProviderScoringInputConfig,
  ScoringInputConfig,
} from "fond/cityPlanner/assessConfigurationHelper";
import { getAreaBoundingBox } from "fond/cityPlanner/helper";
import { MultiProject } from "fond/types";
import { BBox } from "fond/types/geometry";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AccordionStepper, AccordionStepperPane, NonIdealState, StackedNavigation } from "fond/widgets";

import { PlanPanel } from "../PlanPanel";

type IProps = {
  /**
   * The project currently being viewed / edited
   */
  multiProject: MultiProject;
  /**
   * Flag indicating if the project is read only
   */
  readOnly: boolean;
};

const AutoDesignPanel: React.FC<IProps> = ({ multiProject, readOnly }: IProps) => {
  const multiProjectBoundingBox: BBox | null = useMemo(
    () => (multiProject?.Areas ? getAreaBoundingBox(multiProject.Areas) : null),
    [multiProject?.Areas]
  );

  const { data: broadbandProviders } = useGetBroadbandProvidersQuery(
    multiProject && multiProjectBoundingBox
      ? {
          accountId: multiProject.Account.ID,
          bounds: multiProjectBoundingBox,
          // The assess step only consider _fiber_ coverage, so only include those providers here
          serviceType: "fiber",
        }
      : skipToken
  );

  const { scoringInputConfigs, candidateScoringInputConfigs } = useMemo(() => {
    const localScoringInputConfigs: ScoringInputConfig[] = [...baseScoringInputConfigs];
    const localCandidateScoringInputConfigs: ScoringInputConfig[] = [];
    const seenProviders = new Set<string>();

    // Any providers in the assess configuration are included as genuine scoring inputs.
    for (const provider in multiProject?.AssessConfiguration?.ProviderFiberCoverageImportance ?? {}) {
      seenProviders.add(provider);
      localScoringInputConfigs.push(createProviderScoringInputConfig(provider));
    }

    // Other broadband providers in the multi-project's bounding box are included as candidates.
    for (const provider of broadbandProviders ?? []) {
      if (!seenProviders.has(provider.Name)) {
        seenProviders.add(provider.Name);
        localCandidateScoringInputConfigs.push(createProviderScoringInputConfig(provider.Name));
      }
    }

    return {
      scoringInputConfigs: localScoringInputConfigs,
      candidateScoringInputConfigs: localCandidateScoringInputConfigs,
    };
  }, [broadbandProviders, multiProject?.AssessConfiguration?.ProviderFiberCoverageImportance]);

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Work area",
        id: "subareas",
        content: <AreaPanel multiProject={multiProject} />,
        isComplete: multiProject.Areas.length > 0,
      },
      {
        header: "Assess",
        id: "assess",
        content: <AssessPanel multiProject={multiProject} scoringInputConfigs={scoringInputConfigs} />,
        isComplete: multiProject.Areas.length > 0 && assessConfigurationHasValues(multiProject.AssessConfiguration, scoringInputConfigs),
        rightAdornment: null,
        optional: true,
      },
      {
        header: "Plan",
        id: "plan",
        content: <PlanPanel multiProject={multiProject} readOnly={readOnly} />,
        isComplete: multiProject.Areas.some((area) => area.Project?.ID),
      },
    ],
    [multiProject, scoringInputConfigs, readOnly]
  );

  const cityPlannerStackedNavigationPanels: Array<{ id: string; name: string; component: React.ReactNode; useDefaultHeader?: boolean }> = useMemo(
    () => [
      {
        id: "areaDraw",
        name: "Subareas",
        component: <AreaDrawPanel multiProject={multiProject} />,
        useDefaultHeader: false,
      },
      {
        id: "scoring",
        name: "Scoring Inputs",
        component: <ScoringPanel candidateScoringInputConfigs={candidateScoringInputConfigs} scoringInputConfigs={scoringInputConfigs} />,
        useDefaultHeader: true,
      },
      {
        id: "subareaList",
        name: "Subareas",
        component: <SubareaListPanel multiProject={multiProject} />,
        useDefaultHeader: true,
      },
    ],
    [scoringInputConfigs, candidateScoringInputConfigs, multiProject]
  );

  if (!multiProject) return null;

  return (
    <Box data-testid="city-planner-design-panel" sx={{ height: "100%" }}>
      {!permissionCheck(multiProject.Permission?.Level, Actions.CITY_PLANNER_EDIT) ? ( // if user has access and no license, more granular "locks" are applied per component
        <NonIdealState size="small" icon={<Lock />} title="View only access" description="You only have view access to this project" />
      ) : (
        <StackedNavigation
          rootComponent={<AccordionStepper id={multiProject.ID} panes={accordionPanes} allowCollapse autoMove="next" />}
          screens={cityPlannerStackedNavigationPanels}
        />
      )}
    </Box>
  );
};

export default AutoDesignPanel;
